import { useState, useCallback } from "react";

/**
 * Custom hook for managing a counter that persists across component remounts
 * using sessionStorage (resets when browser tab is closed)
 *
 * @param key - Unique key for storing the counter value in sessionStorage
 * @param initialValue - Initial value for the counter (default: 0)
 * @returns [value, setValue, reset] - Current value, setter function, and reset function
 */
export function usePersistentCounter(key: string, initialValue: number = 0) {
  const [value, setValue] = useState(() => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored ? parseInt(stored, 10) : initialValue;
    } catch (error) {
      console.warn(
        `Failed to read from sessionStorage for key "${key}":`,
        error
      );
      return initialValue;
    }
  });

  const setPersistentValue = useCallback(
    (newValue: number | ((prev: number) => number)) => {
      setValue((prev) => {
        const result =
          typeof newValue === "function" ? newValue(prev) : newValue;
        try {
          sessionStorage.setItem(key, result.toString());
        } catch (error) {
          console.warn(
            `Failed to write to sessionStorage for key "${key}":`,
            error
          );
        }
        return result;
      });
    },
    [key]
  );

  const reset = useCallback(() => {
    setValue(initialValue);
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn(
        `Failed to remove from sessionStorage for key "${key}":`,
        error
      );
    }
  }, [key, initialValue]);

  return [value, setPersistentValue, reset] as const;
}
